import { type Dispatch, type FC, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useStorePrice } from '@/common/hooks/apple-price';
import { type BuyCoinItemProps } from '@/common/types/components';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { StringMain, StringSmall } from '@/components/Typography/Typography';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { VipIcon } from '@/components/ui/Icons';

import css from './styles.module.scss';

interface OfferItemProps {
  item: BuyCoinItemProps;
  id: number;
  price: number;
  tgem: number;
  baseTgem?: number;
  isActive?: boolean;
  isFirstPurchase?: boolean;
  bonus?: number;
  vip?: number;
  isPopular?: boolean;
  changeActiveOffer: Dispatch<SetStateAction<BuyCoinItemProps | undefined>>;
}

const OfferItem: FC<OfferItemProps> = ({
  item,
  id,
  price,
  tgem,
  baseTgem,
  isActive,
  isFirstPurchase,
  bonus,
  vip,
  isPopular,
  changeActiveOffer,
}) => {
  const storePrice = useStorePrice(price);
  const { t } = useTranslation();

  return (
    <div
      key={id}
      onClick={() => changeActiveOffer(item)}
      className="relative w-[calc(100%_-_2rem)] mx-4"
    >
      {isPopular && (
        <div className="absolute -top-[4px] right-0 z-[4]">
          <Ticker variant="red-small" text={t('popular')} />
        </div>
      )}
      {isActive && <div className={cn(css.effect, css.effectFirst)}></div>}
      {isActive && <div className={cn(css.effect, css.effectSecond)}></div>}
      <div
        className={cn(
          css.offerItem,
          isActive && css['offerItem--active'],
          isActive && css['offerItem--active'],
        )}
      >
        {isActive && <div className={css.elipse}></div>}

        <div
          className={cn(
            css.gridWrapper,
            isFirstPurchase && css.gridWrapperFirst,
          )}
        >
          <div
            className={cn(css.checkbox, isActive && css['checkbox--active'])}
          ></div>
          <div className="flex items-center gap-1">
            <CurrencyImage size="1.5rem" kind="tgem" />
            {isFirstPurchase && (
              <StringSmall
                weight="medium"
                className={cn(
                  '!text-white/40 relative xl:text-sm',
                  css['crossed-out'],
                )}
              >
                {baseTgem}
              </StringSmall>
            )}
            <StringMain weight="bold" className="!leading-1.2 xl:text-sm">
              {tgem}
            </StringMain>
          </div>
          {bonus ? (
            <StringSmall
              weight="medium"
              className="!text-gold mr-2 xl:text-xs !leading-none"
            >{`${bonus}% ${t('Bonus')}`}</StringSmall>
          ) : (
            <div></div>
          )}
          {vip ? (
            <div className={css.vip}>
              <StringSmall
                weight="bold"
                className="!text-gold xl:text-xs !leading-none"
              >
                +{vip}
              </StringSmall>
              <span className="text-gold leading-none font-normal text-[10px]">
                {t('days')}
              </span>
              <VipIcon className="absolute top-0 right-0 h-full aspect-square xl:size-6 size-8" />
            </div>
          ) : (
            <div></div>
          )}
          <div className="flex justify-end items-center gap-[6px]">
            {/* <TelegramStarIcon className="text-gold" /> */}
            <StringMain weight="bold" className="!text-nowrap xl:text-sm">
              {storePrice ?? `$ ${price.toFixed(2)}`}
            </StringMain>
          </div>
        </div>
      </div>
    </div>
  );
};

export { OfferItem };
